// src/hooks/useResponsive.ts
import { useState, useEffect } from "react";

interface ResponsiveState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  windowWidth: number;
  windowHeight: number;
}

const BREAKPOINTS = {
  mobile: 902,
  tablet: 1024,
} as const;

export function useResponsive(): ResponsiveState {
  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  }));

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, 100);
    };

    window.addEventListener("resize", handleResize, { passive: true });
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return {
    isMobile: windowSize.width < BREAKPOINTS.mobile,
    isTablet:
      windowSize.width >= BREAKPOINTS.mobile &&
      windowSize.width < BREAKPOINTS.tablet,
    isDesktop: windowSize.width >= BREAKPOINTS.tablet,
    windowWidth: windowSize.width,
    windowHeight: windowSize.height,
  };
}

// Fixed useResponsiveValue hook
export function useResponsiveValue<T>(values: [T, T, T]): T {
  const { isMobile, isTablet } = useResponsive();

  if (isMobile) return values[0];
  if (isTablet) return values[1];
  return values[2];
}
